/** Positions of two items, one in each sequence, that are matched as the same item. */
export interface ISequenceMatch {
  firstIndex: number,
  secondIndex: number,
}

export type TSequenceItemComparator<FIRST, SECOND> = (firstItem: FIRST, secondItem: SECOND, firstIndex: number, secondIndex: number) => boolean;

/**
 * Matches the items of two sequences along their longest common subsequence, in increasing order
 * of both indices.
 *
 * Where several matchings are equally long, items are matched as early as possible, so an item
 * replaced in place stays unmatched next to its replacement instead of being matched to a later
 * equal item.
 */
export function matchSequences<FIRST, SECOND>(
  first: readonly FIRST[],
  second: readonly SECOND[],
  areItemsEqual: TSequenceItemComparator<FIRST, SECOND>,
): ISequenceMatch[] {
  const columnCount = second.length + 1;
  // Length of the longest common subsequence of first[i..] and second[j..], at i * columnCount + j.
  const suffixLengths = new Uint32Array((first.length + 1) * columnCount);
  const equalities = new Uint8Array(first.length * second.length);

  for (let i = first.length - 1; i >= 0; i--) {
    for (let j = second.length - 1; j >= 0; j--) {
      const areEqual = areItemsEqual(first[i], second[j], i, j);
      equalities[i * second.length + j] = areEqual ? 1 : 0;
      suffixLengths[i * columnCount + j] = areEqual
        ? suffixLengths[(i + 1) * columnCount + j + 1] + 1
        : Math.max(suffixLengths[(i + 1) * columnCount + j], suffixLengths[i * columnCount + j + 1]);
    }
  }

  const matches: ISequenceMatch[] = [];
  let firstIndex = 0;
  let secondIndex = 0;
  while (firstIndex < first.length && secondIndex < second.length) {
    if (equalities[firstIndex * second.length + secondIndex] === 1) {
      matches.push({
        firstIndex,
        secondIndex,
      });
      firstIndex++;
      secondIndex++;
    } else if (suffixLengths[(firstIndex + 1) * columnCount + secondIndex] >= suffixLengths[firstIndex * columnCount + secondIndex + 1]) {
      firstIndex++;
    } else {
      secondIndex++;
    }
  }
  return matches;
}
